import { Label, ErrorMessage, RequiredAsterisk } from '@atlaskit/form';
import InfoIcon from '@atlaskit/icon/core/migration/information--editor-panel';
import { Box, Inline } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { messages as globalMessages } from '@databases/intl/messages';
import { ViewId } from '@databases/types/database/view';
import React, { FunctionComponent, useState, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import useView from '../../hooks/useView';
import { messages } from '../../i18n';

export interface ViewSettingsOverviewProps {
  viewNameIsValid: boolean;
  viewNames: string[];
  canEditViews: boolean;
  viewName: string;
  setViewName: (value: string) => void;
}

export const ViewRename: FunctionComponent<ViewSettingsOverviewProps> = ({
  viewNameIsValid,
  canEditViews,
  viewName,
  setViewName,
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const { view } = useView();

  const [isFieldFocused, setIsFieldFocused] = useState(false);

  const handleBlurEvent = () => {
    if (viewName.trim() === '') {
      setViewName(view.name);
    }
    setIsFieldFocused(false);
  };

  const handleFocusEvent = () => {
    setIsFieldFocused(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setViewName(e.target.value);
  };

  const errorAttributes: { [key: string]: string } = {};
  if (!isFieldFocused) {
    errorAttributes['aria-relevant'] = 'all';
    errorAttributes['aria-atomic'] = 'false';
  }

  const translatedViewName = useMemo(() => {
    if (view.id === ViewId.UNSAVED) {
      return formatMessage(globalMessages['view.unsaved-view']);
    }

    return view.name;
  }, [formatMessage, view.id, view.name]);

  return (
    <Box>
      <Inline alignBlock="center" space="space.025">
        <Label htmlFor="basic-textfield">
          {formatMessage(messages['view-settings-panel.name-view-label'])}
          <RequiredAsterisk />
        </Label>

        {view.id === ViewId.UNSAVED && (
          <Tooltip
            content={formatMessage(
              messages['view-settings-panel.unsaved-rename-tooltip']
            )}
            position={'right'}
          >
            <InfoIcon
              label={formatMessage(messages['view-settings-panel.rename-info-label'])}
              LEGACY_size="medium"
              spacing="spacious"
              color={token('color.icon')}
            />
          </Tooltip>
        )}
      </Inline>
      <Textfield
        name="basic"
        id="basic-textfield"
        testId="view-rename-textfield"
        placeholder={formatMessage(messages['view-settings-panel.name-view-placeholder'])}
        isDisabled={view.id === ViewId.UNSAVED || !canEditViews}
        value={viewName}
        defaultValue={translatedViewName}
        isInvalid={!viewNameIsValid}
        onBlur={handleBlurEvent}
        onChange={handleChange}
        onFocus={handleFocusEvent}
      />
      {!viewNameIsValid && (
        <ErrorMessage>
          <Box aria-live="polite" {...errorAttributes}>
            {formatMessage(messages['view-rename.unique-view-requirement'])}
          </Box>
        </ErrorMessage>
      )}
    </Box>
  );
};
